import authService from './auth.service';
import type {
  AgentConversation,
  AgentConversationListResponse,
  SendMessageResponse,
  ConfirmActionResponse,
  CancelActionResponse,
} from '../types/agent.types';

const API_URL = '/api/v1/agent/conversations';

async function handleResponse<T>(res: Response, errorMsg: string): Promise<T> {
  if (!res.ok) {
    if (res.status === 429) {
      throw new Error('Слишком много запросов. Подождите немного.');
    }
    const err = await res.json().catch(() => null);
    throw new Error(err?.detail || errorMsg);
  }
  return (await res.json()) as T;
}

class AgentService {
  async createConversation(metadata?: Record<string, unknown>): Promise<AgentConversation> {
    const res = await authService.fetchWithAuth(API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ metadata: metadata ?? {} }),
    });
    return handleResponse<AgentConversation>(res, 'Не удалось создать диалог');
  }

  async listConversations(params?: { limit?: number; offset?: number; status?: string }): Promise<AgentConversationListResponse> {
    const qs = new URLSearchParams();
    qs.set('limit', String(params?.limit ?? 20));
    qs.set('offset', String(params?.offset ?? 0));
    if (params?.status) qs.set('status', params.status);
    const res = await authService.fetchWithAuth(`${API_URL}?${qs}`);
    return handleResponse<AgentConversationListResponse>(res, 'Не удалось загрузить диалоги');
  }

  async getConversation(id: number): Promise<AgentConversation> {
    const res = await authService.fetchWithAuth(`${API_URL}/${id}`);
    return handleResponse<AgentConversation>(res, 'Не удалось загрузить диалог');
  }

  async sendMessage(conversationId: number, content: string): Promise<SendMessageResponse> {
    const res = await authService.fetchWithAuth(`${API_URL}/${conversationId}/messages`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content }),
    });
    return handleResponse<SendMessageResponse>(res, 'Не удалось отправить сообщение');
  }

  async confirmAction(conversationId: number): Promise<ConfirmActionResponse> {
    const res = await authService.fetchWithAuth(`${API_URL}/${conversationId}/confirm`, {
      method: 'POST',
    });
    return handleResponse<ConfirmActionResponse>(res, 'Не удалось подтвердить действие');
  }

  async cancelAction(conversationId: number): Promise<CancelActionResponse> {
    const res = await authService.fetchWithAuth(`${API_URL}/${conversationId}/cancel-action`, {
      method: 'POST',
    });
    return handleResponse<CancelActionResponse>(res, 'Не удалось отменить действие');
  }

  async deleteConversation(conversationId: number): Promise<void> {
    const res = await authService.fetchWithAuth(`${API_URL}/${conversationId}`, {
      method: 'DELETE',
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось завершить диалог');
    }
  }
}

const agentService = new AgentService();
export default agentService;
